import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader } from 'equal-ds-ui';
import { Download, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Alert } from '../../../components/ui/Alert';
import { downloadAPI } from '../../../services/download-api.service';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';
import type { DownloadJob } from '../../../types/download';

const FILE_TYPE_EXTENSIONS: Record<string, string> = {
  EXCEL: '.xlsx',
  PDF: '.pdf',
  CSV: '.csv',
  XML: '.xml',
  ZIP: '.zip',
};

const DOWNLOAD_TYPE_LABELS: Record<string, string> = {
  FI_DATA: 'FI Data Report',
  FI_NOTIFICATIONS: 'FI Notifications Report',
  CONSENT_LIST: 'Consent List Report',
  DATA_PURGING: 'Data Purging Report',
  FI_RAW_DATA: 'FI Raw Data',
  EWS_REPORT: 'EWS Report',
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  SUCCESS: { label: 'Completed', color: '#16a34a' },
  PROCESSING: { label: 'Processing', color: '#ca8a04' },
  PENDING: { label: 'Pending', color: '#ca8a04' },
  FAILED: { label: 'Failed', color: '#dc2626' },
};

function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const day = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${time}, ${day}`;
  } catch {
    return isoString;
  }
}

interface DownloadJobCardProps {
  job: DownloadJob;
}

const DownloadJobCard: React.FC<DownloadJobCardProps> = ({ job }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  const extension = FILE_TYPE_EXTENSIONS[job.file_type] || '.file';
  const title = DOWNLOAD_TYPE_LABELS[job.download_type] || job.download_type;
  const fileName = job.file_name || title;
  const displayName = `${title} - ${fileName}`;
  const statusInfo = STATUS_LABELS[job.status] || { label: job.status, color: '#6b7280' };

  const subtitle = [
    `Generated at ${formatTimestamp(job.created_at)}`,
    job.created_by ? job.created_by : null,
  ].filter(Boolean).join(' - ');

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(false);
    try {
      const response = await downloadAPI.getDownloadUrl(job.job_id);
      const url = response?.data?.download_url;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title}${extension}`;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch {
      setDownloadError(true);
      setTimeout(() => setDownloadError(false), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
      }}
    >
      {/* File type badge */}
      <div
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#e7edf0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#666d78', letterSpacing: '0.28px' }}>
          {extension}
        </span>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <span
            style={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#0f3340',
              letterSpacing: '0.32px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
            title={displayName}
          >
            {displayName}
          </span>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: statusInfo.color,
              background: `${statusInfo.color}15`,
              padding: '2px 8px',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {statusInfo.label}
          </span>
        </div>
        <span style={{ fontSize: '12px', color: '#666d78', letterSpacing: '0.24px' }}>
          {subtitle}
        </span>
        {downloadError && (
          <span style={{ fontSize: '11px', color: '#dc2626' }}>Download failed. Try again.</span>
        )}
      </div>

      {/* Download button */}
      {job.status === 'SUCCESS' ? (
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          style={{
            background: '#0f3340',
            color: 'white',
            border: '1px solid #021d27',
            borderBottom: '2px solid #021d27',
            borderRadius: '8px',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: isDownloading ? 'not-allowed' : 'pointer',
            opacity: isDownloading ? 0.6 : 1,
            flexShrink: 0,
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: '0.24px',
            whiteSpace: 'nowrap',
          }}
        >
          {isDownloading ? (
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Download size={16} />
          )}
          DOWNLOAD
        </button>
      ) : job.status === 'PROCESSING' || job.status === 'PENDING' ? (
        <div
          style={{
            background: '#f5f5f5',
            color: '#999',
            borderRadius: '8px',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0,
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: '0.24px',
          }}
        >
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          GENERATING
        </div>
      ) : null}
    </div>
  );
};

export const DownloadCentrePage: React.FC<PageComponentProps> = () => {
  const [jobs, setJobs] = useState<DownloadJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [tokenHistory, setTokenHistory] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const { error, errorMessage, handleError, clearError } = useErrorHandler();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const currentPageRef = useRef(currentPage);
  const tokenHistoryRef = useRef(tokenHistory);
  currentPageRef.current = currentPage;
  tokenHistoryRef.current = tokenHistory;

  const fetchJobs = useCallback(async (token?: string, showLoading = false) => {
    clearError();
    if (showLoading) setIsLoading(true);
    try {
      const response = await downloadAPI.listJobs(pageSizeRef.current, token);
      setJobs(response?.data?.jobs ?? []);
      setNextToken(response?.data?.next_token || undefined);
    } catch (err) {
      handleError(err, { component: 'DownloadCentrePage', action: 'fetchJobs' });
      setJobs([]);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [clearError, handleError]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await downloadAPI.listJobs(pageSize);
        if (!cancelled) {
          setJobs(response?.data?.jobs ?? []);
          setNextToken(response?.data?.next_token || undefined);
        }
      } catch (err) {
        if (!cancelled) handleError(err, { component: 'DownloadCentrePage', action: 'fetchJobs' });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [handleError, pageSize]);

  useEffect(() => {
    const hasActiveJobs = jobs.some((j) => j.status === 'PENDING' || j.status === 'PROCESSING');
    if (hasActiveJobs) {
      pollingRef.current = setInterval(() => {
        const token = currentPageRef.current === 1
          ? undefined
          : tokenHistoryRef.current[tokenHistoryRef.current.length - 1];
        fetchJobs(token);
      }, 10000);
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobs, fetchJobs]);

  const goToNextPage = useCallback(() => {
    if (!nextToken) return;
    setTokenHistory(prev => [...prev, nextToken]);
    setCurrentPage(prev => prev + 1);
    fetchJobs(nextToken, true);
  }, [nextToken, fetchJobs]);

  const goToPrevPage = useCallback(() => {
    if (currentPage <= 1) return;
    const newHistory = [...tokenHistory];
    newHistory.pop();
    setTokenHistory(newHistory);
    setCurrentPage(prev => prev - 1);
    const prevToken = newHistory.length > 0 ? newHistory[newHistory.length - 1] : undefined;
    fetchJobs(prevToken, true);
  }, [currentPage, tokenHistory, fetchJobs]);

  const refresh = async () => {
    setIsRefreshing(true);
    setCurrentPage(1);
    setTokenHistory([]);
    setNextToken(undefined);
    await fetchJobs(undefined, true);
    setIsRefreshing(false);
  };

  const hasPrev = currentPage > 1;
  const hasNext = !!nextToken;

  return (
    <div className="equal-ds-root p-6 bg-gray-50 min-h-screen">
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Download Centre</h1>
          <p className="text-gray-600 mt-2">
            Find all your downloadable reports in one place
          </p>
        </div>
        <button
          onClick={refresh}
          style={{
            background: 'white',
            border: '1px solid #d9f0ff',
            borderRadius: '8px',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            color: '#0f3340',
          }}
        >
          <RefreshCw size={16} style={isRefreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
          Refresh
        </button>
      </div>

      {errorMessage && (
        <Alert variant="error" className="mb-6">
          <div className="flex flex-col gap-1">
            <span>{errorMessage}</span>
            {error?.message && <span className="text-xs opacity-80">{error.message}</span>}
          </div>
        </Alert>
      )}

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' }}>
          <Loader />
        </div>
      ) : jobs.length === 0 && !errorMessage ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px', color: '#999' }}>
          <p style={{ fontSize: '18px', fontWeight: 500 }}>No downloads yet</p>
          <p style={{ fontSize: '14px', marginTop: '4px' }}>Downloads you request will appear here</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {jobs.map((job) => (
              <DownloadJobCard key={job.job_id} job={job} />
            ))}
          </div>

          {/* Pagination */}
          {(hasPrev || hasNext) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '16px',
                paddingTop: '12px',
                borderTop: '1px solid #e7edf0',
              }}
            >
              <button
                onClick={goToPrevPage}
                disabled={!hasPrev}
                style={{
                  background: hasPrev ? 'white' : '#f5f5f5',
                  border: '1px solid #d9f0ff',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: hasPrev ? 'pointer' : 'not-allowed',
                  opacity: hasPrev ? 1 : 0.5,
                  fontSize: '13px',
                  color: '#0f3340',
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#666d78', fontWeight: 500 }}>
                Page {currentPage}
              </span>
              <button
                onClick={goToNextPage}
                disabled={!hasNext}
                style={{
                  background: hasNext ? 'white' : '#f5f5f5',
                  border: '1px solid #d9f0ff',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: hasNext ? 'pointer' : 'not-allowed',
                  opacity: hasNext ? 1 : 0.5,
                  fontSize: '13px',
                  color: '#0f3340',
                }}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
